import { browserLogger } from "@@/settings";
import { EmptyState } from "@@/shared/components/empty-state";
import { Row } from "@@/shared/components/row";
import { VirtualizedList } from "@@/shared/components/virtualized-list";
import { currentTimezoneName } from "@@/shared/current-timezone-name";
import { useIsMountedRef } from "@@/shared/use-is-mounted-ref";
import type { IsoAndUnixTimestamp, TimeRange, Timezone } from "@towni/common";
import {
    asDate,
    dateRangeFactory,
    dateRangeUtils,
    isoAndUnixFactory,
    nowIsoAndUnix,
    sortBy,
    toFullDate,
    toZonedTime,
} from "@towni/common";
import { isFuture, isSameDay, isToday } from "date-fns";
import { fromZonedTime } from "date-fns-tz";
import React, { useCallback, useEffect, useRef } from "react";
import type { VirtuosoHandle } from "react-virtuoso";
import { HzItem } from "./hz-item";
import type { DateData, HzItemData } from "./hz-item-data";

const findClosestMatchingDay = (
    closestTo: IsoAndUnixTimestamp,
    source: IsoAndUnixTimestamp[],
) => {
    const currentDate = asDate(closestTo);
    const sameDays = source.filter(d => isSameDay(currentDate, asDate(d)));
    if (sameDays.length === 1) return sameDays[0];

    //Get closest matching day
    const current = closestTo.unix;
    const sorted = source.sort(sortBy(date => date.unix));
    const closestNowAndInTheFuture = sorted.find(date => date.unix >= current);
    const closestNowAndInThePast = closestNowAndInTheFuture
        ? undefined
        : sorted[sorted.length - 1];
    return closestNowAndInTheFuture ?? closestNowAndInThePast;
};

//If you don't understand this, DON'T ASK ME
const getFullKeyInProviderTimeZone = (
    date: IsoAndUnixTimestamp,
    timeZone: Timezone,
) => {
    const dateZoned = fromZonedTime(
        toZonedTime(asDate(date), currentTimezoneName),
        timeZone,
    );

    return toFullDate(dateZoned, timeZone);
};

const EmptyPlaceholder = () => (
    <Row className="size-full p-5">
        <EmptyState
            className="bg-neutral-surface-10 flex-4 text-center"
            text={{
                sv: "Inga datum",
                en: "No dates",
            }}
        />
    </Row>
);
const getItemKey = (_: number, item: HzItemData) => {
    return `${item._type === "DATA" ? toFullDate(item.date) : item.index}_ITEM`;
};

type Props = {
    readonly timeRange: TimeRange | undefined;
    readonly selectableDates: IsoAndUnixTimestamp[];
    readonly onSelect: (date: IsoAndUnixTimestamp | undefined) => void;
    readonly initiallySelected?: IsoAndUnixTimestamp | Date;
    readonly showMonthHeader?: boolean;
    readonly spin?: boolean;
    /**
     * Always force one date to be selected
     * @type {boolean}
     */
    readonly forceSelection?: boolean;
    readonly initialScrollToToday?: boolean;
    readonly dataTestId?: string;
};

const DateSelectionHzSingle = (props: Props) => {
    const spin = props.spin || !props.timeRange;
    const listRef = useRef<VirtuosoHandle | null>(null);
    const [scrollToColumn, setScrollToColumn] = React.useState<
        number | undefined
    >();

    const datesInTimeRange = React.useMemo(() => {
        const timeRange = props.timeRange;

        if (!timeRange) return [];
        const range = dateRangeFactory.fromTimeRange({
            timeRange: timeRange,
            timezone: "Europe/Stockholm" as Timezone,
        });

        const dateRangeDates = dateRangeUtils.getDates(range);

        return dateRangeDates;
    }, [props.timeRange]);

    const [selectedDate, setSelectedDate] = React.useState<
        IsoAndUnixTimestamp | undefined
    >();

    const isSelected = useCallback(
        (date: IsoAndUnixTimestamp) =>
            selectedDate && isSameDay(asDate(selectedDate), asDate(date)),
        [selectedDate],
    );

    const onSelectRef = useRef(props.onSelect);
    onSelectRef.current = props.onSelect;
    const toggleDateSelected = React.useCallback(
        (date: IsoAndUnixTimestamp | undefined) => {
            if (props.forceSelection && typeof date === "undefined") return;

            browserLogger.debug("datesInToggleDateSelected", {
                date,
            });

            setSelectedDate(date);
            onSelectRef.current(date);
        },
        [props.forceSelection],
    );

    const renderItem = useCallback(
        (index: number, item: HzItemData) => {
            const isFirst = index === 0;
            const isLast = index === datesInTimeRange.length - 1;

            const className = (() => {
                if (isFirst) return "ml-5 mr-2";
                if (isLast) return "mr-5";
                return "mr-2";
            })();

            return (
                <HzItem
                    key={getItemKey(index, item)}
                    data={item}
                    index={index}
                    className={className}
                    dataLength={datesInTimeRange.length}
                    toggler={toggleDateSelected}
                />
            );
        },
        [datesInTimeRange.length, toggleDateSelected],
    );

    const datesWithinTimeRangeInfo = React.useMemo(() => {
        const dates = new Set(
            props.selectableDates
                .map(date => asDate(date))
                .filter(Boolean)
                .map(date => toFullDate(date)),
        );

        const data = datesInTimeRange.map((date): DateData => {
            // const zoned =
            const canBeBooked = dates.has(
                getFullKeyInProviderTimeZone(
                    date,
                    "Europe/Stockholm" as Timezone,
                ),
            );
            return {
                _type: "DATA",
                date,
                selected: isSelected(date),
                canBeBooked: canBeBooked ? asDate(date) : undefined,
            };
        });

        const filtered = data.reduce((agg, item, index) => {
            if (item.canBeBooked !== undefined) {
                agg.push(item);
                return agg;
            }

            const previous = agg.length > 0 && agg[agg.length - 1];
            const next = index !== data.length && data[index + 1];

            if (
                !previous ||
                (previous._type !== "SQUASH" && previous.canBeBooked) ||
                index === data.length - 1
            ) {
                agg.push(item);
                return agg;
            }

            if (previous._type === "SQUASH" && next && !next.canBeBooked) {
                return agg;
            }
            if (
                previous._type !== "SQUASH" &&
                !previous.canBeBooked &&
                next &&
                !next.canBeBooked
            ) {
                agg.push({ _type: "SQUASH", index: index });
                return agg;
            }

            agg.push(item);

            return agg;
        }, [] as HzItemData[]);
        //If we have scrolled to a date, we should not scroll again
        if (scrollToColumn) return filtered;
        //First time scroll to selected, after never scroll as the selected should always be in the window
        const index = filtered.findIndex(d => d._type === "DATA" && d.selected);
        if (index > 0 && undefined === scrollToColumn) setScrollToColumn(index);
        else {
            //Scroll to today if nothing is selected
            const todayIndex = filtered.findIndex(
                d => d._type === "DATA" && isToday(asDate(d.date)),
            );
            if (todayIndex === -1) {
                //Find closet to today
                const indexFirstFuture = filtered.findIndex(
                    d => d._type === "DATA" && isFuture(asDate(d.date)),
                );
                if (indexFirstFuture !== scrollToColumn)
                    setScrollToColumn(indexFirstFuture);
            }
            if (todayIndex !== scrollToColumn) setScrollToColumn(todayIndex);
        }

        return filtered;
    }, [props.selectableDates, datesInTimeRange, scrollToColumn, isSelected]);

    // Do stuff after first load of data
    const isMountedRef = useIsMountedRef();
    const hasBeenLoaded = useRef(false);
    const scrollCommandTimeout = useRef(null);
    useEffect(() => {
        if (hasBeenLoaded.current) return;
        if (spin) return;
        hasBeenLoaded.current = true;
        if (
            !props.initiallySelected &&
            !props.forceSelection &&
            !props.initialScrollToToday
        )
            return;
        const origin = isoAndUnixFactory(
            props.initiallySelected ?? nowIsoAndUnix(),
        );
        const closest = findClosestMatchingDay(origin, datesInTimeRange);
        if (closest) {
            if (props.forceSelection || !!props.initiallySelected) {
                setSelectedDate(closest);
            }

            // Since the list is not mounted yet always,
            // we need to wait for it to be mounted before scrolling,
            // sometimes, otherwise nothing to scroll to
            const scroll = (depth = 0) => {
                return setTimeout(() => {
                    if (depth > 30) return;
                    if (!isMountedRef.current) return;
                    if (!listRef.current) scroll(depth + 1);
                    listRef.current?.scrollToIndex({
                        index: datesWithinTimeRangeInfo.findIndex(
                            item =>
                                item._type === "DATA" &&
                                item.date.unix === closest.unix,
                        ),
                        offset: -20,
                    });
                }, 100);
            };
            if (scrollCommandTimeout.current)
                clearTimeout(scrollCommandTimeout.current);
            scroll();
        }
    }, [
        spin,
        datesWithinTimeRangeInfo,
        props.forceSelection,
        props.initiallySelected,
        props.initialScrollToToday,
        datesInTimeRange,
        isMountedRef,
    ]);

    return (
        <Row
            data-testid={props.dataTestId}
            mainAxis="start"
            className="h-23 w-full flex-shrink-0 overflow-x-auto">
            <VirtualizedList
                ref={listRef}
                className="flex-1"
                key="provider-product-list"
                data={datesWithinTimeRangeInfo}
                itemContent={renderItem}
                horizontalDirection
                computeItemKey={getItemKey}
                increaseViewportBy={{
                    top: 100,
                    bottom: 200,
                }}
                components={{
                    EmptyPlaceholder,
                }}
            />
        </Row>
    );
};

export { DateSelectionHzSingle };
